import { useState } from 'react';
import { useAuth } from 'contexts/AuthContext';
import { toast } from 'sonner';
import { ImagePlus, Trash2 } from 'lucide-react';
import { DEFAULT_BRANDING, brandingVars } from 'components/ThemeProvider';

const COLOR_FIELDS = [
  ['primary_color', 'Color principal'],
  ['secondary_color', 'Color secundario'],
  ['accent_color', 'Acento'],
  ['bg_color', 'Fondo'],
  ['text_color', 'Texto'],
];

const assetUrl = (u) => {
  if (!u) return null;
  const i = u.indexOf('/static/branding/');
  const path = i >= 0 ? u.slice(i) : u;
  return /^https?:/.test(path) ? path : `${process.env.REACT_APP_API_URL || ''}${path}`;
};

const LABELS = {
  logo_top: 'Logo esquina superior',
  logo_bottom: 'Logo esquina inferior',
  background: 'Imagen de fondo',
};

const ImageSlot = ({ kind, label, url, onUpload, onRemove }) => (
  <div>
    <p className="text-xs mb-2 opacity-70">{label}</p>
    <div className="h-24 rounded-lg border border-white/10 bg-black/40 flex items-center justify-center overflow-hidden">
      {url ? <img src={assetUrl(url)} alt={label} className="max-h-full max-w-full object-contain" />
           : <span className="text-xs opacity-40">Sin imagen</span>}
    </div>
    <div className="flex gap-2 mt-2">
      <label className="flex-1 cursor-pointer text-center text-xs py-2 rounded-lg border border-white/15 hover:bg-white/5">
        <ImagePlus className="inline w-3.5 h-3.5 mr-1" /> {url ? 'Cambiar' : 'Subir'}
        <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
               onChange={(e) => { onUpload(kind, e.target.files?.[0]); e.target.value = ''; }} />
      </label>
      {url && (
        <button onClick={() => onRemove(kind)} className="px-3 rounded-lg border border-white/15 hover:bg-white/5">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  </div>
);

const OrgBrandingEditor = ({ org, onSaved }) => {
  const { api } = useAuth();
  const [branding, setBranding] = useState({ ...DEFAULT_BRANDING, ...(org.branding || {}) });
  const [imgs, setImgs] = useState({
    logo_top: org.logo_top_url || org.logo_url || null,
    logo_bottom: org.logo_bottom_url || null,
    background: org.bg_image_url || null,
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setBranding((b) => ({ ...b, [k]: v }));

  const upload = async (kind, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error('Máximo 5 MB');
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await api.post(`/superadmin/organizations/${org.id}/branding/image/${kind}`, fd);
      setImgs((p) => ({ ...p, [kind]: data.url }));
      toast.success(`${LABELS[kind]} actualizado`);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error subiendo la imagen');
    }
  };

  const remove = async (kind) => {
    try {
      await api.delete(`/superadmin/organizations/${org.id}/branding/image/${kind}`);
      setImgs((p) => ({ ...p, [kind]: null }));
      toast.success(`${LABELS[kind]} eliminado`);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al eliminar');
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/superadmin/organizations/${org.id}/branding`, branding);
      toast.success('Identidad corporativa guardada');
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const vars = brandingVars(branding);

  return (
    <div className="grid md:grid-cols-2 gap-6">
      {/* Controles */}
      <div className="space-y-5">
        <div className="grid grid-cols-2 gap-3">
          {COLOR_FIELDS.map(([key, label]) => (
            <label key={key} className="flex items-center gap-3 text-sm">
              <input type="color" value={branding[key]} onChange={(e) => set(key, e.target.value)}
                     className="w-10 h-10 rounded cursor-pointer bg-transparent border-0" />
              <span>
                {label}
                <span className="block text-[10px] opacity-50 font-mono">{branding[key]}</span>
              </span>
            </label>
          ))}
        </div>

        <label className="block text-sm">
          Oscurecido del fondo: {Math.round(branding.bg_overlay * 100)}%
          <input type="range" min="0" max="1" step="0.02" value={branding.bg_overlay}
                 onChange={(e) => set('bg_overlay', parseFloat(e.target.value))} className="w-full" />
        </label>

        <div className="grid grid-cols-2 gap-4">
          <ImageSlot kind="logo_top" label={`${LABELS.logo_top} (PNG transparente)`}
                     url={imgs.logo_top} onUpload={upload} onRemove={remove} />
          <ImageSlot kind="logo_bottom" label={`${LABELS.logo_bottom} (PNG transparente)`}
                     url={imgs.logo_bottom} onUpload={upload} onRemove={remove} />
        </div>
        <ImageSlot kind="background" label={LABELS.background}
                   url={imgs.background} onUpload={upload} onRemove={remove} />

        <div className="flex gap-2">
          <button onClick={save} disabled={saving}
                  className="px-5 py-2 rounded-lg text-sm font-bold text-black disabled:opacity-50"
                  style={{ background: branding.primary_color }}>
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
          <button onClick={() => setBranding(DEFAULT_BRANDING)}
                  className="px-4 py-2 rounded-lg text-sm border border-white/15">
            Restablecer colores
          </button>
        </div>
      </div>

      {/* Vista previa en vivo */}
      <div className="relative rounded-xl overflow-hidden min-h-[320px] border border-white/10" style={vars}>
        <div className="absolute inset-0" style={{ background: branding.bg_color }} />
        {imgs.background && <div className="absolute inset-0"
          style={{ backgroundImage: `url('${assetUrl(imgs.background)}')`, backgroundSize: 'cover', backgroundPosition: 'center' }} />}
        <div className="absolute inset-0" style={{ background: branding.bg_color, opacity: branding.bg_overlay }} />

        {imgs.logo_top && <img src={assetUrl(imgs.logo_top)} alt="" className="absolute top-3 right-4 h-9 max-w-[40%] object-contain" />}
        {imgs.logo_bottom && <img src={assetUrl(imgs.logo_bottom)} alt="" className="absolute bottom-3 right-4 h-9 max-w-[40%] object-contain" />}

        <div className="relative p-6 pt-14" style={{ color: branding.text_color }}>
          <h3 className="text-xl font-bold" style={{ color: branding.primary_color }}>{org.name}</h3>
          <p className="text-sm opacity-80 mb-4">Así verán la plataforma tus empleados</p>
          <div className="flex gap-2">
            <span className="px-4 py-2 rounded-lg text-sm font-bold text-black"
                  style={{ background: `linear-gradient(135deg, ${branding.primary_color}, ${branding.secondary_color})` }}>
              Botón principal
            </span>
            <span className="px-4 py-2 rounded-lg text-sm font-bold text-black"
                  style={{ background: branding.accent_color }}>Acento</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrgBrandingEditor;